import { useEffect, useState } from 'react';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch } from 'react-redux';
import { Box, Stack } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import TextInput from 'components/Common/TextInput';
import AuthLayaout from './AuthLayout';
import { notify } from 'components/Common/Toast';
import { store } from 'redux/store';
import { login } from 'redux/slices/auth/authSlice';
import MuiLoader from 'components/Common/Loader';
import Routes from 'constants/routes';

import './Auth.css';
import CustomButton from 'components/Common/CustomButton';
import { getAuthData } from 'utils/helpers';
import TwoFactorAuthentication from './TwoFactorAuthentication';
import TwoFactorVerifyPin from './TwoFactorVerifyPin';

const formSchema = yup
    .object({
        username: yup.string().required('Email is required').trim(),
        password: yup.string().required('Password is required'),
    })
    .required();

export interface LoginFormInputs {
    username: string;
    password: string;
}

enum modalType {
    AUTH_TYPE = 'authType',
    VERIFY_PIN = 'verifyPin',
}

function Login() {
    const authData = getAuthData();
    const { access_token, need_change_password } = authData || {};
    // maintained this state for initial loading incase user is authenticated and enter the login route to navigate the user to home
    const [initialLoading, setInitialLoading] = useState<boolean>(false);
    const navigate = useNavigate();
    const dispatch = useDispatch<typeof store.dispatch>();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isModal, setIsModal] = useState<modalType | null>(null);
    const [userEmail, setUserEmail] = useState<any>(null);
    const [tempAuthResponse, setTempAuthResponse] = useState(null);

    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm<LoginFormInputs>({
        resolver: yupResolver(formSchema),
    });

    useEffect(() => {
        if (need_change_password) {
            return navigate(Routes.account.changePassword);
        }
        if (access_token) {
            navigate(Routes.dashboard.main);
        }
        setInitialLoading(false);
    }, []);

    const submit = async (data: { [x: string]: any }) => {
        const username = data?.username;
        const password = data?.password;
        setIsLoading(true);

        try {
            let result: any = await dispatch(login({ username, password }));
            if (result?.error || !result?.payload) return;
            const { is2FARequired, response } = result?.payload || {};
            const { need_change_password } = response || {};

            if (is2FARequired) {
                setIsModal(modalType.AUTH_TYPE);
                setUserEmail(username);
                setTempAuthResponse(response);
                return;
            }
            notify('Logged in successfully!', 'success');
            if (need_change_password) {
                navigate(Routes.auth.changePassword);
            } else {
                navigate(Routes.dashboard.main);
            }
        } catch (error: any) {
            notify(error.message, 'error');
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    };

    const onSendEmail = () => {
        setIsModal(modalType.VERIFY_PIN);
    };

    const handleVerifyPinAndCleanup = () => {
        localStorage.setItem('token', JSON.stringify(tempAuthResponse));
        setUserEmail(null);
        setIsModal(null);
        setTempAuthResponse(null);
        navigate(Routes.dashboard.main);
    };

    if (initialLoading) {
        return <MuiLoader absolute loading={initialLoading} />;
    }

    return (
        <>
            {isModal === modalType.AUTH_TYPE && <TwoFactorAuthentication userEmail={userEmail} onSendEmail={onSendEmail} />}
            {isModal === modalType.VERIFY_PIN && userEmail && (
                <TwoFactorVerifyPin username={userEmail} verifyPinCallback={handleVerifyPinAndCleanup} />
            )}
            {!isModal && (
                <AuthLayaout>
                    <form onSubmit={handleSubmit(submit)} className="Auth-form">
                        <div>
                            <p className="Auth-form-title">Welcome, Old Glory Nation</p>
                            <p className="Auth-form-subtitle">Sign in to continue</p>
                        </div>
                        <Box>
                            <Controller
                                name="username"
                                control={control}
                                render={({ field }) => (
                                    <TextInput
                                        {...field}
                                        sx={{ mt: 2, mb: 2 }}
                                        icon={null}
                                        label="Email"
                                        placeholder="Email"
                                        error={!!errors?.username}
                                        helperText={(errors.username as any)?.message}
                                    />
                                )}
                            />
                            <Controller
                                name="password"
                                control={control}
                                render={({ field }) => (
                                    <TextInput
                                        {...field}
                                        icon={null}
                                        sx={{ mt: 2, mb: 2 }}
                                        type="password"
                                        placeholder="Password"
                                        error={!!errors?.password}
                                        helperText={(errors.password as any)?.message}
                                        label="Password"
                                    />
                                )}
                            />
                            <Stack width="100%" alignItems="end">
                                <CustomButton
                                    fontSize={14}
                                    variant="text"
                                    onClick={() => navigate(Routes.auth.forgotPassword)}
                                    height="fit-content"
                                    sx={{ '&:hover': { backgroundColor: 'transparent' }, p: '0px' }}
                                >
                                    Forgot Password?
                                </CustomButton>
                            </Stack>
                        </Box>

                        <CustomButton
                            fontSize={20}
                            type="submit"
                            disabled={isLoading}
                            loading={isLoading}
                            title="SIGN IN"
                            onClick={handleSubmit(submit)}
                        />
                    </form>
                </AuthLayaout>
            )}
        </>
    );
}

export default Login;
